import React, { useCallback, useMemo } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import {
  AutoComplete,
  BoxV4,
  NoteV2,
  TextAreaV4,
  TextInputV4,
} from '@labrav/react-components'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { debounce, get } from 'lodash'
import { useFlagValue } from '@labrav/flags'
import { useSelector } from '../../../../store'
import { useStyles } from './AuthorizationDetails.styles'
import { ResaleAuthorizationWithErrors } from '../../../../modules/resaleAuthorization/types'
import { OfferRecipientV3 } from '../../../../common/components/OfferRecipientV3/OfferRecipientV3'
import { OfferRecipientWithTable } from '../../../../common/components/OfferRecipientWithTable/OfferRecipientWithTable'
import { ResellerInfo } from '../../../../types/resaleAuthorization'
import {
  FieldMetadata,
  FieldMetadataField,
  OfferRecipients,
} from '../../../../modules/privateOffer/types'
import type { PrivateOfferDetailsSectionProps } from '../../types'

export type { PrivateOfferDetailsSectionProps }
export {
  privateOfferExpirationDateField,
  privateOfferSubscriptionEndDateField,
} from '../../constants'
type ResellerOption = {
  key: string
  label: string
  /** Present when resale authorization reseller search flag is enabled */
  searchLabel?: string
}
type ResellerOptionOrFreeText =
  | string
  | { key?: string; label?: string; inputValue?: string }
  | null
export const AuthorizationDetails: React.FC<
  PrivateOfferDetailsSectionProps
> = ({ product, noteDescription }) => {
  const formikProps =
    useFormikContext() as FormikProps<ResaleAuthorizationWithErrors>
  const { values, errors, touched, handleBlur, setFieldValue } = formikProps

  const { value: resellerSearchEnabled } = useFlagValue(
    'resaleAuthorizationSearchAndAddNewReseller'
  )

  const fieldMetadata = useSelector<FieldMetadata | undefined>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        product?.productId as string
      ]?.fieldMetadata
  )

  const isAuthorizationNameReadOnly = (
    fieldMetadata?.agreementName as FieldMetadataField
  )?.readOnly
  const isAuthorizationNameHidden = (
    fieldMetadata?.agreementName as FieldMetadataField
  )?.hidden

  const isBuyerNameReadOnly = (fieldMetadata?.companyName as FieldMetadataField)
    ?.readOnly
  const isBuyerNameHidden = (fieldMetadata?.companyName as FieldMetadataField)
    ?.hidden

  const isResellerAwsAccountIdReadOnly = (
    fieldMetadata?.resellerAwsAccountId as FieldMetadataField
  )?.readOnly
  const isResellerAwsAccountIdHidden = (
    fieldMetadata?.resellerAwsAccountId as FieldMetadataField
  )?.hidden

  const isAuthorizationDescriptionReadOnly = (
    fieldMetadata?.agreementDescription as FieldMetadataField
  )?.readOnly
  const isAuthorizationDescriptionHidden = (
    fieldMetadata?.agreementDescription as FieldMetadataField
  )?.hidden

  const isCustomEmailMessageReadOnly = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.readOnly
  const isCustomEmailMessageHidden = (
    fieldMetadata?.emailCustomMessage as FieldMetadataField
  )?.hidden

  const isAuthorizationRecipientsFromTemplate =
    (
      (fieldMetadata?.agreementRecipientsList as Record<string, unknown>)
        ?.agreementRecipients as OfferRecipients[]
    )?.length > 0 || false

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    [setFieldValue]
  )

  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )

  const resellers = useSelector<ResellerInfo[]>(
    state => state.resellers.resellers
  )

  const resellerNameCounts = useMemo(() => {
    return (
      resellers?.reduce(
        (nameCounts, reseller) => {
          const resellerName = reseller.resellerCompanyName as string
          nameCounts[resellerName] = (nameCounts[resellerName] || 0) + 1
          return nameCounts
        },
        {} as Record<string, number>
      ) || {}
    )
  }, [resellers])

  const resellerOptions: ResellerOption[] = useMemo(() => {
    return (
      resellers?.map(reseller => {
        const resellerName = reseller.resellerCompanyName as string
        const resellerId = reseller.labraPlatformResellerId as string
        const hasDuplicateName = resellerNameCounts[resellerName] > 1
        return {
          key: resellerId,
          label: hasDuplicateName
            ? `${resellerName} (${resellerId})`
            : resellerName,
          ...(resellerSearchEnabled ? { searchLabel: resellerName } : {}),
        }
      }) || []
    )
  }, [resellers, resellerNameCounts, resellerSearchEnabled])

  const resellerAutocompleteValue = useMemo(() => {
    const selected = values.resellersInfo?.[0]
    if (!selected?.labraPlatformResellerId) return null

    const match = resellerOptions.find(
      opt => opt.key === selected.labraPlatformResellerId
    )
    if (match) return match

    if (resellerSearchEnabled) {
      return (
        (selected.resellerCompanyName as string) ||
        (selected.labraPlatformResellerId as string) ||
        null
      )
    }

    return null
  }, [resellerOptions, resellerSearchEnabled, values.resellersInfo])

  const selectedResellerFromList = useMemo(() => {
    const selectedResellerId =
      values.resellersInfo?.[0]?.labraPlatformResellerId
    if (!selectedResellerId) return undefined
    return resellers?.find(
      r => r.labraPlatformResellerId === selectedResellerId
    )
  }, [resellers, values.resellersInfo])

  const isResellerAccountIdLocked = useMemo(() => {
    return Boolean(selectedResellerFromList)
  }, [selectedResellerFromList])

  const handleEnhancedResellerChange = useCallback(
    (_event: unknown, option: ResellerOptionOrFreeText) => {
      if (!option) {
        setFieldValue('resellersInfo', [])
        return
      }

      const optionKey = typeof option === 'object' ? option.key : undefined
      const optionLabel =
        typeof option === 'string'
          ? option
          : (option?.label ?? option?.inputValue ?? '')

      if (optionKey) {
        const selectedReseller = resellers.find(
          r => r.labraPlatformResellerId === optionKey
        )

        if (selectedReseller) {
          setFieldValue('resellersInfo', [selectedReseller])
        } else {
          setFieldValue('resellersInfo', [
            {
              labraPlatformResellerId: optionKey,
              resellerAwsAccountId: '',
              resellerCompanyName: optionLabel,
            },
          ])
        }
        return
      }

      if (optionLabel) {
        setFieldValue('resellersInfo', [
          {
            labraPlatformResellerId: optionLabel,
            resellerAwsAccountId: '',
            resellerCompanyName: optionLabel,
          },
        ])
        return
      }

      setFieldValue('resellersInfo', [])
    },
    [resellers, setFieldValue]
  )

  const onResellerAutocompleteChange = useCallback(
    (
      event: React.SyntheticEvent,
      newValue:
        | string
        | { key?: string; label?: string; inputValue?: string }
        | null
    ) => {
      handleEnhancedResellerChange(event, newValue as ResellerOptionOrFreeText)
    },
    [handleEnhancedResellerChange]
  )

  const { classes } = useStyles()
  return (
    <div data-testid="details-section">
      <BoxV4
        heading={'Authorization details'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {noteDescription && <NoteV2 description={noteDescription} />}
        {/* company details */}
        <div className={classes.sectionContainer}>
          <div className={classes.companyDetailsContainer}>
            {!isAuthorizationNameHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="agreementName"
                  title="Authorization name"
                  type="text"
                  placeHolder=""
                  value={(values.agreementName || '') as string}
                  isError={
                    !!(
                      get(touched, 'agreementName', false) &&
                      get(errors, 'agreementName', false)
                    )
                  }
                  onChange={formChangeHandler('agreementName')}
                  isReadOnly={isAuthorizationNameReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'agreementName', false) &&
                      get(errors, 'agreementName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
            {!isBuyerNameHidden && (
              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="companyName"
                  title="Buyer name (Optional)"
                  type="text"
                  placeHolder=""
                  value={values.companyName as string}
                  maxNumber={100}
                  isError={
                    !!(
                      get(touched, 'companyName', false) &&
                      get(errors, 'companyName', false)
                    )
                  }
                  onChange={formChangeHandler('companyName')}
                  isReadOnly={isBuyerNameReadOnly || false}
                  isRequired={false}
                  errorText={
                    (!!get(touched, 'companyName', false) &&
                      get(errors, 'companyName', ' ')) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            )}
          </div>
        </div>
        {!isResellerAwsAccountIdHidden && (
          <div className={classes.sectionContainer}>
            <div className={classes.companyDetailsContainer}>
              <div className={classes.resellerNameAutocompleteContainer}>
                <AutoComplete
                  id={`resellersInfo`}
                  label={'Reseller name'}
                  options={resellerOptions}
                  value={resellerAutocompleteValue}
                  freeSolo={Boolean(resellerSearchEnabled)}
                  forcePopupIcon={true}
                  onChange={onResellerAutocompleteChange}
                  onBlur={handleBlur}
                  error={
                    !!(
                      get(touched, 'resellersInfo', false) &&
                      typeof get(errors, 'resellersInfo', false) === 'string'
                    )
                  }
                  helperText={
                    get(touched, 'resellersInfo', false) &&
                    typeof get(errors, 'resellersInfo', undefined) === 'string'
                      ? (get(errors, 'resellersInfo', '') as string)
                      : ' '
                  }
                  size="small"
                  filterableOptions={
                    resellerSearchEnabled
                      ? ({
                          options,
                          inputValue,
                        }: {
                          options: ResellerOption[]
                          inputValue: string
                        }) => {
                          const normalizedInput = inputValue
                            .trim()
                            .toLowerCase()
                          if (!normalizedInput) return options
                          return options.filter(option =>
                            (option.searchLabel ?? option.label)
                              .toLowerCase()
                              .startsWith(normalizedInput)
                          )
                        }
                      : undefined
                  }
                  disabled={isResellerAwsAccountIdReadOnly || false}
                />
              </div>

              <div className={classes.inputWidth}>
                <TextInputV4
                  keyName="resellersInfo[0].resellerAwsAccountId"
                  title="Reseller account ID"
                  type="text"
                  placeHolder=""
                  value={
                    (values?.resellersInfo?.[0]?.resellerAwsAccountId ||
                      '') as string
                  }
                  maxNumber={12}
                  updatedValue={
                    (values?.resellersInfo?.[0]
                      ?.resellerAwsAccountId as string) || ''
                  }
                  isError={
                    !!(
                      get(
                        touched,
                        'resellersInfo[0].resellerAwsAccountId',
                        false
                      ) &&
                      get(
                        errors,
                        'resellersInfo[0].resellerAwsAccountId',
                        false
                      )
                    )
                  }
                  onChange={(
                    event: React.ChangeEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >
                  ) => {
                    const digitsOnly = event.target.value
                      .replace(/\D/g, '')
                      .slice(0, 12)
                    setFieldValue(
                      'resellersInfo[0].resellerAwsAccountId',
                      digitsOnly
                    )
                  }}
                  isReadOnly={
                    isResellerAwsAccountIdReadOnly || isResellerAccountIdLocked
                  }
                  isRequired={false}
                  errorText={
                    (!!get(
                      touched,
                      'resellersInfo[0].resellerAwsAccountId',
                      false
                    ) &&
                      get(
                        errors,
                        'resellersInfo[0].resellerAwsAccountId',
                        ' '
                      )) ||
                    ' '
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
        )}
        {!isAuthorizationDescriptionHidden && (
          <div className={classes.sectionContainer}>
            <TextAreaV4
              keyName="agreementDescription"
              title="Authorization description (Optional)"
              rows={10}
              maxNumber={255}
              type="string"
              placeHolder="Authorization description (Optional)"
              value={(values.agreementDescription || '') as string}
              isError={
                !!(errors.agreementDescription && touched.agreementDescription)
              }
              onChange={formChangeHandler('agreementDescription')}
              isReadOnly={isAuthorizationDescriptionReadOnly || false}
              isRequired={false}
              errorText={
                (!!touched.agreementDescription &&
                  errors.agreementDescription) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        )}

        {isAuthorizationRecipientsFromTemplate ? (
          <>
            <OfferRecipientWithTable
              offerRecipients={
                (
                  fieldMetadata?.agreementRecipientsList as Record<
                    string,
                    unknown
                  >
                )?.agreementRecipients as OfferRecipients[]
              }
              formik={
                formikProps as unknown as FormikProps<Record<string, unknown>>
              }
              heading="Authorization recipients"
              keyName="agreementRecipients"
            />
          </>
        ) : (
          <>
            <div
              className={classes.sectionContainer}
              data-testid="recipients-details"
            >
              <div className={classes.textContainer}>
                <Typography className={classes.sectionHeading}>
                  Authorization recipients
                </Typography>
              </div>
              <OfferRecipientV3
                formik={
                  formikProps as unknown as FormikProps<Record<string, unknown>>
                }
                isReadOnly={false}
                keyName="agreementRecipients"
              />
            </div>
          </>
        )}

        {/* custom message */}
        {!isCustomEmailMessageHidden && (
          <div className={classes.sectionContainer}>
            <TextAreaV4
              keyName="emailCustomMessage"
              title="Add custom email message (Optional)"
              rows={10}
              maxNumber={1000}
              // height={100}
              type="string"
              placeHolder="Add custom email message (Optional)"
              value={(values.emailCustomMessage || '') as string}
              isError={
                !!(errors.emailCustomMessage && touched.emailCustomMessage)
              }
              onChange={formChangeHandler('emailCustomMessage')}
              isReadOnly={isCustomEmailMessageReadOnly || false}
              isRequired={false}
              errorText={
                (!!touched.emailCustomMessage && errors.emailCustomMessage) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
            />
          </div>
        )}
      </BoxV4>
    </div>
  )
}
